import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
  Autocomplete,
  Menu,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import ReportsService from "../../Endpoints/ReportFull";
import ReportSummarizeService from "../../Endpoints/ReportSummarize";
import brokersService from "../../Endpoints/BrokersIBs";
import BrokerTreesService from "../../Endpoints/BrokerTrees";
import IconComponent from "../../components/Icons/IconComponent";
import ExportComponent from "../../components/Exports/ExporterFiles";
import ExportIconButton from "../Generals/Buttons/ExportIconButton";
import * as BrokerStyles from "../Broker/Styles.js";
import {
  BROKERS_IBS_REPORT_SEARCH_ID_STORAGE_KEY,
  buildTableRowsPerPageOptions,
  TABLE_BODY_ROW_HEIGHT,
  TABLE_COMPACT_HEADER_HEIGHT,
  TABLE_COMPACT_HEADER_PADDING,
  TABLE_HEADER_FONT_SIZE,
  TABLE_INITIAL_ROWS_PER_PAGE,
} from "../Table/constants.js";
import {
  PageContainer,
  TitleWrap,
  CardContainer,
  FiltersContainer,
  TabsContainer,
  TableContainerStyled,
  EmptyState,
  FooterContainer,
} from "./Styles";

const TRANSACTION_OPTIONS = ["DEPOSIT", "WITHDRAW", "PAYOUT"];
const ALL_BROKERS_VALUE = "ALL_BROKERS";
const ALL_IBS_VALUE = "ALL_IBS";
const ALL_TREES_VALUE = "ALL_TREES";
const REPORTS_FILTERS_STORAGE_KEY = "reports_filters_state_v1";
const REPORTS_CLIENT_RESPONSE_STORAGE_KEY = "reports_client_response_v1";
const REPORTS_FULL_ROWS_STORAGE_KEY = "reports_full_rows_v1";
const REPORTS_SUMMARY_ROWS_STORAGE_KEY = "reports_summary_rows_v1";
const REPORTS_SEARCH_STORAGE_KEY = "reports_search_value_v1";

const PERIODS = [
  "Today",
  "Last 3 days",
  "Last week",
  "Last month",
  "Last 3 months",
  "Last 6 months",
  "Custom date",
];

const PERIOD_OPTIONS = PERIODS.map((value) => ({ label: value, value }));

const ROWS_OPTIONS = buildTableRowsPerPageOptions([5, 10, 25, 50, 100]);
const ALL_TRANSACTION_TYPES = "ALL";

const FULL_EXPORT_COLUMNS = [
  { field: "date", headerName: "Date", label: "Date" },
  { field: "ibId", headerName: "IB ID", label: "IB ID" },
  { field: "name", headerName: "Name", label: "Name" },
  { field: "email", headerName: "Email", label: "Email" },
  { field: "type", headerName: "Type", label: "Type" },
  { field: "amount", headerName: "Amount", label: "Amount" },
  { field: "volume", headerName: "Volume", label: "Volume" },
  { field: "currency", headerName: "CCY", label: "CCY" },
  { field: "platform", headerName: "Platform", label: "Platform" },
  { field: "account", headerName: "Account", label: "Account" },
  { field: "ticket", headerName: "Ticket", label: "Ticket" },
  { field: "comments", headerName: "Comments", label: "Comments" },
];

const SUMMARY_EXPORT_COLUMNS = [
  { field: "date", headerName: "Date", label: "Date" },
  { field: "ibId", headerName: "IB ID", label: "IB ID" },
  { field: "name", headerName: "Name", label: "Name" },
  { field: "email", headerName: "Email", label: "Email" },
  { field: "walletCurrency", headerName: "Wallet CCY", label: "Wallet CCY" },
  { field: "walletBalance", headerName: "Wallet Balance", label: "Wallet Balance" },
  { field: "deposits", headerName: "Deposits", label: "Deposits" },
  { field: "withdraws", headerName: "Withdraws", label: "Withdraws" },
  { field: "payouts", headerName: "Payouts", label: "Payouts" },
  { field: "volume", headerName: "Volume", label: "Volume" },
];

const FULL_REPORT_COLUMNS = [
  { field: "date", label: "Date", minWidth: 150, width: 160 },
  { field: "ibId", label: "IB ID", minWidth: 90, width: 100 },
  { field: "name", label: "Name", minWidth: 150, width: 170 },
  { field: "email", label: "Email", minWidth: 220, width: 240 },
  { field: "type", label: "Type", minWidth: 120, width: 130 },
  {
    field: "amount",
    label: "Amount",
    minWidth: 110,
    width: 120,
    renderCell: (params) => {
      const val = params?.value ?? params?.row?.amount;
      const num = Number(val);
      return Number.isFinite(num) ? parseFloat(num.toFixed(4)) : (val ?? "-");
    },
  },
  {
    field: "volume",
    label: "Volume",
    minWidth: 70,
    width: 100,
    renderCell: (params) => {
      const type = String(params?.row?.transaction_type ?? "").trim().toUpperCase();
      const isPayout = type.includes("PAYOUT");

      if (!isPayout) return "-";

      return params?.value ?? 0;
    },
  },
  { field: "currency", label: "CCY", minWidth: 90, width: 100 },
  { field: "platform", label: "Platform", minWidth: 120, width: 130 },
  { field: "account", label: "Account", minWidth: 120, width: 130 },
  { field: "ticket", label: "Ticket", minWidth: 120, width: 130 },
  { field: "comments", label: "Comments", minWidth: 220, width: 260 },
];

const SUMMARY_REPORT_COLUMNS = [
  { field: "date", label: "Date", minWidth: 150, width: 160 },
  { field: "ibId", label: "IB ID", minWidth: 90, width: 100 },
  { field: "name", label: "Name", minWidth: 150, width: 170 },
  { field: "email", label: "Email", minWidth: 220, width: 240 },
  { field: "walletCurrency", label: "Wallet CCY", minWidth: 120, width: 130 },
  { field: "walletBalance", label: "Wallet Balance", minWidth: 130, width: 150 },
  {
    field: "deposits",
    label: "Deposits",
    minWidth: 150,
    width: 170,
    renderCell: (params) =>
      formatSummaryValueByCurrency(params?.row?.summary_per_currency, "deposits"),
  },
  {
    field: "withdraws",
    label: "Withdraws",
    minWidth: 150,
    width: 170,
    renderCell: (params) =>
      formatSummaryValueByCurrency(params?.row?.summary_per_currency, "withdrawals"),
  },
  {
    field: "payouts",
    label: "Payouts",
    minWidth: 150,
    width: 170,
    renderCell: (params) =>
      formatSummaryValueByCurrency(params?.row?.summary_per_currency, "payouts"),
  },
  {
    field: "payout_volume",
    label: "Volume",
    minWidth: 70,
    width: 100,
    renderCell: (params) => {
      const volume =
        params?.row?.payout_volume ??
        params?.row?.summary_per_currency?.[0]?.payout_volume;

      if (volume === undefined || volume === null || Number(volume) === 0) {
        return "-";
      }

      return formatWalletBalance(volume);
    },
  },
];

const buildInitialColumnWidths = (columns) =>
  columns.reduce((widths, column) => {
    widths[column.field] = column.width;
    return widths;
  }, {});

const initialFullReportColumnWidths = buildInitialColumnWidths(FULL_REPORT_COLUMNS);
const initialSummaryReportColumnWidths = buildInitialColumnWidths(SUMMARY_REPORT_COLUMNS);
const reportHeaderCellSx = {
  fontSize: TABLE_HEADER_FONT_SIZE,
  fontWeight: 700,
  color: "rgba(0,0,0,0.70)",
  backgroundColor: "#F9F9F9",
  borderBottom: 0,
  height: TABLE_COMPACT_HEADER_HEIGHT,
  padding: TABLE_COMPACT_HEADER_PADDING,
  lineHeight: "18px",
};
const reportBodyCellSx = {
  height: TABLE_BODY_ROW_HEIGHT,
  padding: "0 0 0 16px",
};
const reportFilterInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    height: 40,
  },
  "& .MuiInputLabel-root": {
    fontSize: 13,
    top: -6,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#15202A",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#15202A",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#15202A",
  },
};
const reportDateSx = {
  width: 180,
  minWidth: 180,
  ...reportFilterInputSx,
  "& .MuiInputBase-input": {
    whiteSpace: "nowrap",
  },
};
const exportButtonSx = {
  textTransform: "none",
  fontWeight: 800,
  fontSize: 15,
  height: "40px",
  minHeight: "40px",
  padding: "12px",
  boxShadow: "none",
  borderRadius: "8px",
  backgroundColor: "#EB0000",
  color: "#FFFFFF",
  borderColor: "#EB0000",
  flexShrink: 0,
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: "#D50000",
    borderColor: "#D50000",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#EB0000",
    color: "#FFFFFF",
    opacity: 0.5,
  },
};

function toUnixSeconds(date) {
  return Math.floor(date.getTime() / 1000);
}

function startOfDay(date) {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0)
  );
}

function endOfDay(date) {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 0)
  );
}

function addDays(date, amount) {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + amount);
  return next;
}

function formatDateInput(date) {
  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, "0");
  const day = `${date.getUTCDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseDateInputUtc(value) {
  const [year, month, day] = String(value).split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function getPeriodRange(selectedPeriod) {
  const now = new Date();

  switch (selectedPeriod) {
    case "Today":
      return { from: startOfDay(now), to: endOfDay(now) };
    case "Last 3 days":
      return { from: startOfDay(addDays(now, -2)), to: endOfDay(now) };
    case "Last week":
      return { from: startOfDay(addDays(now, -6)), to: endOfDay(now) };
    case "Last month":
      return { from: startOfDay(addDays(now, -29)), to: endOfDay(now) };
    case "Last 3 months":
      return { from: startOfDay(addDays(now, -89)), to: endOfDay(now) };
    case "Last 6 months":
      return { from: startOfDay(addDays(now, -179)), to: endOfDay(now) };
    default:
      return null;
  }
}

function getPeriodUnixRange(selectedPeriod, customFrom, customTo) {
  if (selectedPeriod === "Custom date") {
    if (!customFrom || !customTo) return null;

    const fromDate = parseDateInputUtc(customFrom);
    const toDate = parseDateInputUtc(customTo);

    if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
      return null;
    }

    return {
      from: toUnixSeconds(startOfDay(fromDate)),
      to: toUnixSeconds(endOfDay(toDate)),
    };
  }

  const range = getPeriodRange(selectedPeriod);
  if (!range) return null;

  return {
    from: toUnixSeconds(range.from),
    to: toUnixSeconds(range.to),
  };
}

function normalizeStoredPeriod(value) {
  const legacy = {
    last24: "Today",
    last7: "Last week",
    last30: "Last month",
    custom: "Custom date",
  };

  if (PERIODS.includes(value)) return value;
  return legacy[value] || "Today";
}

const formatWalletBalance = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return numeric.toFixed(2);
};

const formatSummaryValueByCurrency = (summaryItems, field) => {
  const items = Array.isArray(summaryItems) ? summaryItems : [];
  if (!items.length) return "-";

  return items
    .map((item) => {
      const currency = item?.currency ?? "-";
      const rawValue = item?.[field];
      const num = Number(rawValue);

      const formattedValue = Number.isFinite(num)
        ? parseFloat(num.toFixed(4))
        : rawValue ?? "-";

      return `${currency}: ${formattedValue}`;
    })
    .join(" | ");
};

const getStoredBrokersIbsReportSearchId = () => {
  try {
    const value = localStorage.getItem(BROKERS_IBS_REPORT_SEARCH_ID_STORAGE_KEY);
    if (!value) return "";

    localStorage.removeItem(BROKERS_IBS_REPORT_SEARCH_ID_STORAGE_KEY);
    return String(value).trim();
  } catch (storageError) {
    console.error("Could not read reports search id from localStorage:", storageError);
    return "";
  }
};

const getStoredReportFilters = () => {
  try {
    const raw = localStorage.getItem(REPORTS_FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (storageError) {
    console.error("Could not read report filters from localStorage:", storageError);
    return null;
  }
};

const getStoredReportSearch = () => {
  try {
    return String(localStorage.getItem(REPORTS_SEARCH_STORAGE_KEY) ?? "").trim();
  } catch (storageError) {
    console.error("Could not read report search from localStorage:", storageError);
    return "";
  }
};

const setStoredReportSearch = (value) => {
  try {
    localStorage.setItem(REPORTS_SEARCH_STORAGE_KEY, String(value ?? ""));
  } catch (storageError) {
    console.error("Could not persist report search in localStorage:", storageError);
  }
};

const clearStoredReportSearch = () => {
  try {
    localStorage.removeItem(REPORTS_SEARCH_STORAGE_KEY);
  } catch (storageError) {
    console.error("Could not clear report search from localStorage:", storageError);
  }
};

const setStoredReportFilters = (filters) => {
  try {
    localStorage.setItem(REPORTS_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (storageError) {
    console.error("Could not persist report filters in localStorage:", storageError);
  }
};

const clearStoredReportFilters = () => {
  try {
    localStorage.removeItem(REPORTS_FILTERS_STORAGE_KEY);
  } catch (storageError) {
    console.error("Could not clear report filters from localStorage:", storageError);
  }
};

const getStoredClientResponse = () => {
  try {
    const raw = localStorage.getItem(REPORTS_CLIENT_RESPONSE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (storageError) {
    console.error("Could not read client response from localStorage:", storageError);
    return null;
  }
};

const setStoredClientResponse = (payload) => {
  try {
    localStorage.setItem(REPORTS_CLIENT_RESPONSE_STORAGE_KEY, JSON.stringify(payload));
  } catch (storageError) {
    console.error("Could not persist client response in localStorage:", storageError);
  }
};

const getStoredReportRows = (tabType) => {
  const storageKey =
    tabType === "summary" ? REPORTS_SUMMARY_ROWS_STORAGE_KEY : REPORTS_FULL_ROWS_STORAGE_KEY;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (storageError) {
    console.error("Could not read report rows from localStorage:", storageError);
    return [];
  }
};

const setStoredReportRows = (tabType, rows) => {
  const storageKey =
    tabType === "summary" ? REPORTS_SUMMARY_ROWS_STORAGE_KEY : REPORTS_FULL_ROWS_STORAGE_KEY;
  try {
    localStorage.setItem(storageKey, JSON.stringify(Array.isArray(rows) ? rows : []));
  } catch (storageError) {
    console.error("Could not persist report rows in localStorage:", storageError);
  }
};

const clearStoredReportRows = () => {
  try {
    localStorage.removeItem(REPORTS_CLIENT_RESPONSE_STORAGE_KEY);
    localStorage.removeItem(REPORTS_FULL_ROWS_STORAGE_KEY);
    localStorage.removeItem(REPORTS_SUMMARY_ROWS_STORAGE_KEY);
  } catch (storageError) {
    console.error("Could not clear report rows from localStorage:", storageError);
  }
};

const formatBrokerLabel = (broker) => {
  const id = String(broker?.id ?? "").trim();
  const name = String(broker?.name ?? "").trim();
  return [id, name].filter(Boolean).join(" - ");
};

const formatIbLabel = (ib) => {
  const id = String(ib?.id ?? "").trim();
  const name = String(ib?.name ?? "").trim();
  return [id, name].filter(Boolean).join(" - ");
};

const normalizeSelectionArray = (value, allValue) => {
  if (Array.isArray(value)) {
    const cleaned = value.map((item) => String(item ?? "").trim()).filter(Boolean);
    if (!cleaned.length) return [allValue];
    const unique = Array.from(new Set(cleaned));
    return unique.includes(allValue) ? [allValue] : unique;
  }

  const single = String(value ?? "").trim();
  if (!single) return [allValue];
  return single === allValue ? [allValue] : [single];
};

const resolveMultiSelectChange = (incomingValue, previousValues, allValue, normalizeItem) => {
  const prev = normalizeSelectionArray(previousValues, allValue);
  const nextRaw = Array.isArray(incomingValue) ? incomingValue : [incomingValue];
  const next = Array.from(
    new Set(nextRaw.map((item) => normalizeItem(item)).filter(Boolean))
  );

  if (!next.length) return [allValue];

  const hadAll = prev.includes(allValue);
  const hasAllNow = next.includes(allValue);

  if (hasAllNow && !hadAll) return [allValue];
  if (hasAllNow && hadAll) {
    const withoutAll = next.filter((item) => item !== allValue);
    return withoutAll.length ? withoutAll : [allValue];
  }

  return next;
};
const Reports = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const [search, setSearch] = useState("");
  const [idSearch, setIdSearch] = useState("");
  const [temp, setTemp] = useState(ALL_BROKERS_VALUE);
  const [selectedIb, setSelectedIb] = useState([ALL_IBS_VALUE]);
  const [selectedTree, setSelectedTree] = useState([ALL_TREES_VALUE]);
  const [brokerOptions, setBrokerOptions] = useState([]);
  const [brokerTrees, setBrokerTrees] = useState([]);
  const [brokerOptionsLoaded, setBrokerOptionsLoaded] = useState(false);
  const [brokerTreesLoaded, setBrokerTreesLoaded] = useState(false);
  const [transactionType, setTransactionType] = useState(ALL_TRANSACTION_TYPES);
  const [filterScope, setFilterScope] = useState("");
  const [showScopeButtons, setShowScopeButtons] = useState(true);
  const [period, setPeriod] = useState("Today");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fullColumnWidths, setFullColumnWidths] = useState(initialFullReportColumnWidths);
  const [summaryColumnWidths, setSummaryColumnWidths] = useState(initialSummaryReportColumnWidths);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportOpen = Boolean(exportAnchorEl);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(TABLE_INITIAL_ROWS_PER_PAGE);
  const didInitialFullFetchRef = useRef(false);
  const [entrySearchId, setEntrySearchId] = useState("");
  const [enteredFromIb, setEnteredFromIb] = useState(false);

  useEffect(() => {
    const prefilledSearch = getStoredBrokersIbsReportSearchId();
    setEntrySearchId(prefilledSearch || "");
    const storedFilters = getStoredReportFilters();
    const storedSearch = getStoredReportSearch();
    const path = String(location.pathname || "").toLowerCase();
    const storedPeriod = normalizeStoredPeriod(storedFilters?.period);

    if (path.endsWith("/sumarize-reports")) {
      setTab(1);
      setRows(getStoredReportRows("summary"));
      setError("");
      if (prefilledSearch) {
        clearStoredReportFilters();
        clearStoredReportSearch();
      }
      setSearch(prefilledSearch ? "" : storedSearch || storedFilters?.search || "");
      setIdSearch(prefilledSearch || storedFilters?.idSearch || "");
      setTemp(prefilledSearch || storedFilters?.temp || ALL_BROKERS_VALUE);
      setSelectedIb(
        prefilledSearch
          ? [ALL_IBS_VALUE]
          : normalizeSelectionArray(storedFilters?.selectedIb, ALL_IBS_VALUE)
      );
      setSelectedTree(
        prefilledSearch
          ? [ALL_TREES_VALUE]
          : normalizeSelectionArray(storedFilters?.selectedTree, ALL_TREES_VALUE)
      );
      setTransactionType(prefilledSearch ? ALL_TRANSACTION_TYPES : storedFilters?.transactionType || ALL_TRANSACTION_TYPES);
      setPeriod(prefilledSearch ? "Today" : storedPeriod);
      setCustomFrom(prefilledSearch ? "" : storedFilters?.customFrom || "");
      setCustomTo(prefilledSearch ? "" : storedFilters?.customTo || "");
      setFilterScope(prefilledSearch ? "" : storedFilters?.filterScope || "");
      setPage(0);
      return;
    }

    if (path.endsWith("/full-report") || path.endsWith("/reports")) {
      setTab(0);
      setRows(getStoredReportRows("full"));
      setError("");
      if (prefilledSearch) {
        clearStoredReportFilters();
        clearStoredReportSearch();
      }
      setSearch(prefilledSearch ? "" : storedSearch || storedFilters?.search || "");
      setIdSearch(prefilledSearch || storedFilters?.idSearch || "");
      setTemp(prefilledSearch || storedFilters?.temp || ALL_BROKERS_VALUE);
      setSelectedIb(
        prefilledSearch
          ? [ALL_IBS_VALUE]
          : normalizeSelectionArray(storedFilters?.selectedIb, ALL_IBS_VALUE)
      );
      setSelectedTree(
        prefilledSearch
          ? [ALL_TREES_VALUE]
          : normalizeSelectionArray(storedFilters?.selectedTree, ALL_TREES_VALUE)
      );
      setTransactionType(prefilledSearch ? ALL_TRANSACTION_TYPES : storedFilters?.transactionType || ALL_TRANSACTION_TYPES);
      setPeriod(prefilledSearch ? "Today" : storedPeriod);
      setCustomFrom(prefilledSearch ? "" : storedFilters?.customFrom || "");
      setCustomTo(prefilledSearch ? "" : storedFilters?.customTo || "");
      setFilterScope(prefilledSearch ? "" : storedFilters?.filterScope || "");
      setPage(0);
    }
  }, [location.pathname]);

  useEffect(() => {
    setStoredReportSearch(search);
  }, [search]);

  useEffect(() => {
    setStoredReportFilters({
      search,
      idSearch,
      temp,
      selectedIb,
      selectedTree,
      transactionType,
      period,
      customFrom,
      customTo,
      filterScope,
      brokerFilterOptions:
        filterScope === "brokers"
          ? brokerOptions.map((broker) => ({
            id: broker.id,
            name: broker.name,
          }))
          : [],
      tab,
    });
  }, [search, idSearch, temp, selectedIb, selectedTree, transactionType, period, customFrom, customTo, filterScope, brokerOptions, tab]);

  useEffect(() => {
    let alive = true;

    const normalizeUserType = (value) => String(value ?? "").trim().toUpperCase();
    const normalizeStatus = (value) => {
      const status = String(value ?? "").trim().toUpperCase();
      if (status === "ENABLED") return "ACTIVE";
      if (status === "DISABLED") return "INACTIVE";
      return status;
    };

    const mapUsersToBrokers = (users) =>
      users
        .map((user) => ({
          id: String(user?.id ?? "").trim(),
          name: String(user?.name ?? "").trim(),
          userType: normalizeUserType(user?.user_type ?? user?.userType ?? user?.type ?? ""),
          status: normalizeStatus(
            user?.account_status ?? user?.status ?? user?.status_backend ?? ""
          ),
          ibs: Array.isArray(user?.ibs)
            ? user.ibs
              .map((ib) => ({
                id: String(ib?.id ?? ib?.userId ?? "").trim(),
                name: String(ib?.name ?? "").trim(),
                trees: Array.isArray(ib?.trees)
                  ? ib.trees
                    .map((tree) => ({
                      id: String(tree?.id ?? tree?.tree_id ?? "").trim(),
                      name: String(tree?.name ?? tree?.tree_name ?? tree?.treeName ?? "").trim(),
                    }))
                    .filter((tree) => tree.id && tree.name)
                  : [],
              }))
              .filter((ib) => ib.id)
            : [],
        }))
        .filter((user) => user.id)
        .filter((user) => user.userType === "BROKER")
        .filter((user) => user.status === "ACTIVE")
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

    const loadBrokers = async () => {
      try {
        const response = await brokersService.BrokersIBs();
        const users = Array.isArray(response?.data?.data?.users)
          ? response.data.data.users
          : [];

        setStoredClientResponse({
          users,
          savedAt: new Date().toISOString(),
        });

        if (!alive) return;
        setBrokerOptions(mapUsersToBrokers(users));
        setBrokerOptionsLoaded(true);
      } catch (loadError) {
        if (!alive) return;

        const cached = getStoredClientResponse();
        const users = Array.isArray(cached?.users) ? cached.users : [];
        setBrokerOptions(users.length ? mapUsersToBrokers(users) : []);
        setBrokerOptionsLoaded(true);
      }
    };

    loadBrokers();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const loadBrokerTrees = async () => {
      try {
        const response = await BrokerTreesService.getBrokerTrees();
        const payload = response?.data?.data ?? response?.data ?? {};
        const trees = Array.isArray(payload?.trees)
          ? payload.trees
          : Array.isArray(payload)
            ? payload
            : [];

        if (!alive) return;
        setBrokerTrees(trees);
        setBrokerTreesLoaded(true);
      } catch (loadError) {
        if (!alive) return;
        setBrokerTrees([]);
        setBrokerTreesLoaded(true);
      }
    };

    loadBrokerTrees();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!String(temp).trim() || temp === ALL_BROKERS_VALUE) return;
    if (!brokerOptions.length) return;

    const selectedAsBroker = brokerOptions.find((broker) => broker.id === String(temp));
    if (selectedAsBroker) return;

    const matchedBrokerByIb = brokerOptions.find((broker) =>
      Array.isArray(broker.ibs) && broker.ibs.some((ib) => ib.id === String(temp))
    );

    if (matchedBrokerByIb) {
      setTemp(matchedBrokerByIb.id);
      setSelectedIb([String(temp)]);
      setSelectedTree([ALL_TREES_VALUE]);
      return;
    }

    setTemp(ALL_BROKERS_VALUE);
    setSelectedIb([ALL_IBS_VALUE]);
    setSelectedTree([ALL_TREES_VALUE]);
  }, [brokerOptions, temp]);

  const selectedBroker = useMemo(() => {
    return brokerOptions.find((broker) => broker.id === String(temp)) ?? null;
  }, [brokerOptions, temp]);

  const brokerFromBrokersIbs = useMemo(() => {
    const normalizedEntryId = String(entrySearchId || idSearch || "").trim();
    if (!normalizedEntryId) return null;

    return brokerOptions.find((broker) => String(broker.id) === normalizedEntryId) ?? null;
  }, [entrySearchId, idSearch, brokerOptions]);

  const canListAllBrokers = filterScope === "brokers";
  const canListAllIbs = filterScope === "ibs";

  const brokerOptionsForSelect = useMemo(() => {
    if (canListAllBrokers) return brokerOptions;
    if (brokerFromBrokersIbs) return [brokerFromBrokersIbs];
    return [];
  }, [brokerFromBrokersIbs, canListAllBrokers, brokerOptions]);

  const selectedBrokerOption = useMemo(() => {
    if (temp === ALL_BROKERS_VALUE) return null;
    return (
      brokerOptionsForSelect.find((broker) => String(broker.id) === String(temp)) ??
      brokerOptions.find((broker) => String(broker.id) === String(temp)) ??
      null
    );
  }, [temp, brokerOptionsForSelect, brokerOptions]);

  const brokerNameComboboxEnabled = Boolean(brokerFromBrokersIbs) || canListAllBrokers;

  const ibFromBrokersIbs = useMemo(() => {
    const normalizedEntryId = String(entrySearchId || idSearch || "").trim();
    if (!normalizedEntryId) return null;

    for (const broker of brokerOptions) {
      const ib = Array.isArray(broker.ibs)
        ? broker.ibs.find((item) => String(item.id) === normalizedEntryId)
        : null;

      if (ib) return ib;
    }

    return null;
  }, [entrySearchId, idSearch, brokerOptions]);

  useEffect(() => {
    const normalizedEntryId = String(entrySearchId || idSearch || "").trim();
    if (!normalizedEntryId || !brokerOptions.length) {
      setEnteredFromIb(false);
      return;
    }

    const matchesBroker = brokerOptions.some((broker) => String(broker.id) === normalizedEntryId);
    const matchesIb = brokerOptions.some(
      (broker) =>
        Array.isArray(broker.ibs) &&
        broker.ibs.some((ib) => String(ib.id) === normalizedEntryId)
    );

    setEnteredFromIb(matchesIb && !matchesBroker);
  }, [entrySearchId, idSearch, brokerOptions]);

  const ibOptionsByBroker = useMemo(() => {
    if (!selectedBroker || !Array.isArray(selectedBroker.ibs)) return [];
    return selectedBroker.ibs;
  }, [selectedBroker]);

  const allIbOptions = useMemo(() => {
    const ibMap = new Map();

    brokerOptions.forEach((broker) => {
      if (!Array.isArray(broker.ibs)) return;

      broker.ibs.forEach((ib) => {
        const id = String(ib?.id ?? "").trim();
        if (!id || ibMap.has(id)) return;

        ibMap.set(id, {
          id,
          name: String(ib?.name ?? "").trim(),
        });
      });
    });

    return Array.from(ibMap.values()).sort((left, right) =>
      formatIbLabel(left).localeCompare(formatIbLabel(right), undefined, {
        sensitivity: "base",
        numeric: true,
      })
    );
  }, [brokerOptions]);

  const ibOptionsForSelect = useMemo(() => {
    return canListAllIbs ? allIbOptions : ibOptionsByBroker;
  }, [canListAllIbs, allIbOptions, ibOptionsByBroker]);

  const selectedIbOptions = useMemo(() => {
    const selectedIds = normalizeSelectionArray(selectedIb, ALL_IBS_VALUE).filter(
      (id) => id !== ALL_IBS_VALUE
    );

    return selectedIds
      .map((id) => ibOptionsForSelect.find((ib) => String(ib.id) === String(id)))
      .filter(Boolean);
  }, [selectedIb, ibOptionsForSelect]);

  const selectedIbIds = useMemo(
    () => normalizeSelectionArray(selectedIb, ALL_IBS_VALUE).filter((id) => id !== ALL_IBS_VALUE),
    [selectedIb]
  );

  const brokerIbIds = useMemo(() => {
    if (!selectedBroker || !Array.isArray(selectedBroker.ibs)) return [];
    return selectedBroker.ibs.map((ib) => String(ib.id ?? "").trim()).filter(Boolean);
  }, [selectedBroker]);

  const allBrokerIbIds = useMemo(() => {
    return brokerOptions
      .flatMap((broker) => (Array.isArray(broker.ibs) ? broker.ibs : []))
      .map((ib) => String(ib.id ?? "").trim())
      .filter(Boolean)
      .filter((id, index, ids) => ids.indexOf(id) === index);
  }, [brokerOptions]);

  const effectiveIbIdsForTrees = useMemo(() => {
    if (selectedIbIds.length) return selectedIbIds;
    if (brokerIbIds.length) return brokerIbIds;
    if (temp === ALL_BROKERS_VALUE) return allBrokerIbIds;
    return brokerIbIds;
  }, [selectedIbIds, brokerIbIds, allBrokerIbIds, temp]);

  const treeOptionsByIb = useMemo(() => {
    if (!effectiveIbIdsForTrees.length) return [];

    const selectedIbSet = new Set(effectiveIbIdsForTrees.map((id) => String(id)));
    const treeMap = new Map();

    brokerTrees.forEach((tree) => {
      const splits = Array.isArray(tree?.splits) ? tree.splits : [];
      const belongs = splits.some((split) =>
        selectedIbSet.has(String(split?.user?.id ?? split?.ibId ?? "").trim())
      );
      if (!belongs) return;

      const treeId = String(tree?.tree_id ?? tree?.id ?? "").trim();
      const treeName = String(tree?.tree_name ?? tree?.name ?? "").trim();
      if (!treeId || !treeName) return;
      if (!treeMap.has(treeId)) treeMap.set(treeId, { id: treeId, name: treeName });
    });

    return Array.from(treeMap.values());
  }, [effectiveIbIdsForTrees, brokerTrees]);

  const getRange = () => {
    const range = getPeriodUnixRange(period, customFrom, customTo);
    if (range) return range;

    const today = getPeriodUnixRange("Today", "", "");
    return today || { from: 0, to: 0 };
  };

  const buildFullPayload = () => {
    const { from, to } = getRange();
    const effectiveIbNumbers = effectiveIbIdsForTrees
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    const selectedTreeNumbers = normalizeSelectionArray(selectedTree, ALL_TREES_VALUE)
      .filter((id) => id !== ALL_TREES_VALUE)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);

    const allTreeIdsForSelectedIb = treeOptionsByIb
      .map((tree) => Number(tree.id))
      .filter((value) => Number.isFinite(value) && value > 0);

    return {
      type: "Full",
      transaction_type:
        transactionType === ALL_TRANSACTION_TYPES ? TRANSACTION_OPTIONS : [transactionType],
      from,
      to,
      ibs: effectiveIbNumbers,
      trees:
        selectedTreeNumbers.length > 0
          ? selectedTreeNumbers
          : effectiveIbNumbers.length > 0
            ? allTreeIdsForSelectedIb
            : [],
    };
  };

  const buildSummaryPayload = () => {
    const { from, to } = getRange();
    const effectiveIbNumbers = effectiveIbIdsForTrees
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    const selectedTreeNumbers = normalizeSelectionArray(selectedTree, ALL_TREES_VALUE)
      .filter((id) => id !== ALL_TREES_VALUE)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);

    const allTreeIdsForSelectedIb = treeOptionsByIb
      .map((tree) => Number(tree.id))
      .filter((value) => Number.isFinite(value) && value > 0);

    return {
      type: "Summary",
      from,
      to,
      ibs: effectiveIbNumbers,
      trees:
        selectedTreeNumbers.length > 0
          ? selectedTreeNumbers
          : effectiveIbNumbers.length > 0
            ? allTreeIdsForSelectedIb
            : [],
    };
  };

  const handleGenerate = async () => {
    if (!brokerOptionsLoaded || !brokerTreesLoaded) return;

    if (period === "Custom date" && (!customFrom || !customTo)) {
      setError("Select a valid period of time.");
      return;
    }

    const range = getPeriodUnixRange(period, customFrom, customTo);
    if (!range) {
      setError("Select a valid period of time.");
      return;
    }

    if (range.from > range.to) {
      setError("From date must be earlier than To date.");
      return;
    }

    setLoading(true);
    setError("");
    setPage(0);

    try {
      if (tab === 0) {
        const payload = buildFullPayload();
        const response = await ReportsService.generateReport(payload);
        if (!response) {
          setError("An error occurred while generating the report.");
          return;
        }
        const fullReport = response?.data?.data?.full_report ?? response?.data?.full_report ?? [];
        setRows(fullReport);
        setStoredReportRows("full", fullReport);
      } else {
        const payload = buildSummaryPayload();
        const response = await ReportSummarizeService.generateSummaryReport(payload);
        if (!response) {
          setError("An error occurred while generating the report.");
          return;
        }
        const summaryReport =
          response?.data?.data?.summary_report ?? response?.data?.summary_report ?? [];
        setRows(summaryReport);
        setStoredReportRows("summary", summaryReport);
      }
    } catch (err) {
      setError(err?.message || "An error occurred while generating the report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab !== 0) {
      didInitialFullFetchRef.current = false;
      return;
    }
    if (didInitialFullFetchRef.current) return;
    if (!brokerOptionsLoaded || !brokerTreesLoaded) return;

    didInitialFullFetchRef.current = true;
    handleGenerate();
  }, [tab, brokerOptionsLoaded, brokerTreesLoaded]);

  const handleClear = () => {
    clearStoredReportFilters();
    clearStoredReportRows();
    setSearch("");
    setIdSearch("");
    setTemp(ALL_BROKERS_VALUE);
    setSelectedIb([ALL_IBS_VALUE]);
    setSelectedTree([ALL_TREES_VALUE]);
    setTransactionType(ALL_TRANSACTION_TYPES);
    setPeriod("Today");
    setCustomFrom("");
    setCustomTo("");
    setRows([]);
    setError("");
    setPage(0);
    setEntrySearchId("");
    setEnteredFromIb(false);
    setFilterScope("");
  };

  const applyFilterScope = (scope) => {
    setFilterScope(scope);
    setIdSearch("");
    setEntrySearchId("");
    setTemp(ALL_BROKERS_VALUE);
    setSelectedIb([ALL_IBS_VALUE]);
    setSelectedTree([ALL_TREES_VALUE]);
    setError("");
    setPage(0);
    setEnteredFromIb(false);
  };

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;

    const term = search.trim().toLowerCase();

    if (tab === 0) {
      return rows.filter((item) => {
        const date = String(item?.date ?? "").toLowerCase();
        const id = String(item?.user?.id ?? "").toLowerCase();
        const name = String(item?.user?.name ?? "").toLowerCase();
        const email = String(item?.user?.email ?? "").toLowerCase();
        const type = String(item?.transaction_type ?? "").toLowerCase();
        const platform = String(item?.platform ?? "").toLowerCase();
        const account = String(item?.account ?? "").toLowerCase();
        const ticket = String(item?.ticket ?? "").toLowerCase();
        const comments = String(item?.comments ?? "").toLowerCase();

        return (
          date.includes(term) ||
          id.includes(term) ||
          name.includes(term) ||
          email.includes(term) ||
          type.includes(term) ||
          platform.includes(term) ||
          account.includes(term) ||
          ticket.includes(term) ||
          comments.includes(term)
        );
      });
    }

    return rows.filter((item) => {
      const date = String(item?.date ?? "").toLowerCase();
      const id = String(item?.user?.id ?? "").toLowerCase();
      const name = String(item?.user?.name ?? "").toLowerCase();
      const email = String(item?.user?.email ?? "").toLowerCase();
      const walletCurrency = String(item?.user?.wallet?.currency ?? "").toLowerCase();
      const walletBalance = String(item?.user?.wallet?.balance ?? "").toLowerCase();

      const summaryText = (item?.summary_per_currency ?? [])
        .map(
          (entry) =>
            `${entry?.currency ?? ""} ${entry?.deposits ?? ""} ${entry?.withdrawals ?? ""} ${entry?.payouts ?? ""}`
        )
        .join(" ")
        .toLowerCase();

      return (
        date.includes(term) ||
        id.includes(term) ||
        name.includes(term) ||
        email.includes(term) ||
        walletCurrency.includes(term) ||
        walletBalance.includes(term) ||
        summaryText.includes(term)
      );
    });
  }, [rows, search, tab]);

  const totalCount = filteredRows.length;

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / rowsPerPage));
  }, [totalCount, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [rowsPerPage, tab]);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const pageItems = useMemo(() => {
    const last = totalPages;
    const current = page + 1;

    if (last <= 5) return Array.from({ length: last }, (_, i) => i + 1);
    if (current <= 3) return [1, 2, 3, 4, "…", last];
    if (current >= last - 2) return [1, "…", last - 3, last - 2, last - 1, last];
    return [1, "…", current - 1, current, current + 1, "…", last];
  }, [page, totalPages]);

  const currentColumns = tab === 0 ? FULL_REPORT_COLUMNS : SUMMARY_REPORT_COLUMNS;
  const currentColumnWidths = tab === 0 ? fullColumnWidths : summaryColumnWidths;

  const tableMinWidth = useMemo(() => {
    return currentColumns.reduce(
      (total, column) => total + (currentColumnWidths[column.field] || column.width),
      0
    );
  }, [currentColumns, currentColumnWidths]);

  const columnCellSx = useMemo(() => {
    return currentColumns.reduce((styles, column) => {
      const width = currentColumnWidths[column.field] || column.width;

      styles[column.field] = {
        width,
        minWidth: column.minWidth,
        maxWidth: width,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      };

      return styles;
    }, {});
  }, [currentColumns, currentColumnWidths]);

  const setWidthsForCurrentTab = useCallback(
    (updater) => {
      if (tab === 0) {
        setFullColumnWidths(updater);
        return;
      }

      setSummaryColumnWidths(updater);
    },
    [tab]
  );

  const handleColumnResizeStart = useCallback(
    (event, column) => {
      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidth = currentColumnWidths[column.field] || column.width;
      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const handlePointerMove = (moveEvent) => {
        const nextWidth = Math.max(column.minWidth, startWidth + moveEvent.clientX - startX);

        setWidthsForCurrentTab((prev) => ({
          ...prev,
          [column.field]: nextWidth,
        }));
      };

      const handlePointerUp = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
    },
    [currentColumnWidths, setWidthsForCurrentTab]
  );

  const handleColumnResizeKeyDown = useCallback(
    (event, column) => {
      const direction = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
      if (!direction) return;

      event.preventDefault();
      event.stopPropagation();

      setWidthsForCurrentTab((prev) => {
        const currentWidth = prev[column.field] || column.width;
        return {
          ...prev,
          [column.field]: Math.max(column.minWidth, currentWidth + direction * 16),
        };
      });
    },
    [setWidthsForCurrentTab]
  );

  const arrowBtnSx = (disabled) => ({
    minWidth: 30,
    width: 30,
    height: 30,
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E7EB",
    color: disabled ? "#9CA3AF" : "#374151",
    boxShadow: "none",
    padding: 0,
    "&:hover": {
      backgroundColor: "#F9FAFB",
      borderColor: "#D1D5DB",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#FFFFFF",
      borderColor: "#E5E7EB",
      color: "#9CA3AF",
      opacity: 1,
    },
  });

  const toFullExportRow = (row) => ({
    date: row?.date ?? "-",
    ibId: row?.user?.id ?? "-",
    name: row?.user?.name ?? "-",
    email: row?.user?.email ?? "-",
    type: row?.transaction_type ?? "-",
    amount: Number.isFinite(Number(row?.amount))
      ? parseFloat(Number(row.amount).toFixed(4))
      : (row?.amount ?? "-"),
    volume: String(row?.transaction_type ?? "").trim().toUpperCase().includes("PAYOUT")
      ? (row?.volume ?? 0)
      : "-",
    currency: row?.currency ?? "-",
    platform: row?.platform ?? "-",
    account: row?.account ?? "-",
    ticket: row?.ticket ?? "-",
    comments: row?.comments ?? "-",
  });

  const toSummaryExportRow = (row) => {
    const volume = row?.payout_volume ?? row?.summary_per_currency?.[0]?.payout_volume;
    return {
      date: row?.date ?? "-",
      ibId: row?.user?.id ?? "-",
      name: row?.user?.name ?? "-",
      email: row?.user?.email ?? "-",
      walletCurrency: row?.user?.wallet?.currency ?? "-",
      walletBalance: formatWalletBalance(row?.user?.wallet?.balance),
      deposits: formatSummaryValueByCurrency(row?.summary_per_currency, "deposits"),
      withdraws: formatSummaryValueByCurrency(row?.summary_per_currency, "withdrawals"),
      payouts: formatSummaryValueByCurrency(row?.summary_per_currency, "payouts"),
      volume: !volume || Number(volume) === 0 ? "-" : formatWalletBalance(volume),
    };
  };

  const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportClose = () => setExportAnchorEl(null);

  const handleExport = (type) => {
    if (!filteredRows.length) {
      handleExportClose();
      return;
    }

    const formattedDate = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const isFull = tab === 0;
    const columns = isFull ? FULL_EXPORT_COLUMNS : SUMMARY_EXPORT_COLUMNS;
    const exportRows = filteredRows.map((row) =>
      isFull ? toFullExportRow(row) : toSummaryExportRow(row)
    );
    const fileName = `${isFull ? "full_report" : "summary_report"}_${formattedDate}`;

    if (type === "csv") ExportComponent.GenerateCSV(columns, exportRows, fileName);
    if (type === "pdf") ExportComponent.GeneratePDF(columns, exportRows, fileName);
    if (type === "txt") ExportComponent.GenerateTXT(columns, exportRows, fileName);

    handleExportClose();
  };

  const isCustomDate = period === "Custom date";

  return (
    <>
      <PageContainer>
        <Button
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate(-1)}
          sx={{ textTransform: "none", mb: 1.5, color: "#111827" }}
        >
          Go back
        </Button>

        <TitleWrap sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontFamily: "Plus Jakarta Sans",
              fontSize: "26px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "140%",
            }}
          >
            Reports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Get clear insights into trading accounts with full and summarized reports.
          </Typography>
        </TitleWrap>

        <CardContainer elevation={2}>
          <FiltersContainer>
            {(tab === 0 || tab === 1) && (
              <>
                {!enteredFromIb && !canListAllIbs && (
                  <Autocomplete
                    size="small"
                    options={brokerOptionsForSelect}
                    value={selectedBrokerOption}
                    disabled={!brokerNameComboboxEnabled}
                    getOptionLabel={(option) => formatBrokerLabel(option)}
                    isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                    noOptionsText="No brokers found"
                    onChange={(_, broker) => {
                      setTemp(broker?.id ?? ALL_BROKERS_VALUE);
                      setSelectedIb([ALL_IBS_VALUE]);
                      setSelectedTree([ALL_TREES_VALUE]);
                      setError("");
                      setPage(0);
                      setEnteredFromIb(false);
                    }}
                    sx={{ width: 220, minWidth: 220, ...reportFilterInputSx }}
                    renderInput={(params) => (
                      <TextField {...params} label="Broker Name" placeholder="All brokers" />
                    )}
                  />
                )}

                {!enteredFromIb && (
                  <Autocomplete
                    multiple
                    size="small"
                    options={ibOptionsForSelect}
                    value={selectedIbOptions}
                    disabled={!canListAllIbs && temp === ALL_BROKERS_VALUE}
                    getOptionLabel={(option) => formatIbLabel(option)}
                    isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                    noOptionsText="No IBs found"
                    onChange={(_, nextIbs) => {
                      const nextIbIds = nextIbs.map((ib) => String(ib?.id ?? "").trim()).filter(Boolean);
                      setSelectedIb(nextIbIds.length ? nextIbIds : [ALL_IBS_VALUE]);
                      setSelectedTree([ALL_TREES_VALUE]);
                      setPage(0);
                    }}
                    sx={{ width: 220, minWidth: 220, ...reportFilterInputSx }}
                    renderInput={(params) => (
                      <TextField {...params} label="IB ID" placeholder="All IBs" />
                    )}
                  />
                )}

                {enteredFromIb && ibFromBrokersIbs && (
                  <TextField
                    select
                    size="small"
                    label="IB ID"
                    value={ibFromBrokersIbs.id}
                    onChange={() => {}}
                    sx={{ width: 220, minWidth: 220, ...reportFilterInputSx }}
                    SelectProps={{
                      renderValue: () => formatIbLabel(ibFromBrokersIbs),
                    }}
                  >
                    <MenuItem value={ibFromBrokersIbs.id}>
                      {formatIbLabel(ibFromBrokersIbs)}
                    </MenuItem>
                  </TextField>
                )}

                <TextField
                  select
                  size="small"
                  label="Tree ID"
                  value={selectedTree}
                  onChange={(e) => {
                    const nextTreeIds = resolveMultiSelectChange(
                      e.target.value,
                      selectedTree,
                      ALL_TREES_VALUE,
                      (value) => String(value ?? "").trim()
                    );
                    setSelectedTree(nextTreeIds.length ? nextTreeIds : [ALL_TREES_VALUE]);
                    setPage(0);
                  }}
                  sx={{ width: 180, minWidth: 180, ...reportFilterInputSx }}
                  disabled={!effectiveIbIdsForTrees.length}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => {
                      const values = Array.isArray(selected) ? selected : [selected];
                      if (values.includes(ALL_TREES_VALUE)) return "All trees";
                      return values.join(", ");
                    },
                  }}
                >
                  <MenuItem value={ALL_TREES_VALUE}>All trees</MenuItem>
                  {treeOptionsByIb.map((tree) => (
                    <MenuItem key={`tree-${tree.id}`} value={tree.id}>
                      {tree.id} - {tree.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Transaction type"
                  value={transactionType}
                  onChange={(e) => {
                    setTransactionType(e.target.value);
                    setPage(0);
                  }}
                  sx={{ width: 160, minWidth: 160, ...reportFilterInputSx }}
                >
                  <MenuItem value={ALL_TRANSACTION_TYPES}>All</MenuItem>
                  {TRANSACTION_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}

            <TextField
              select
              size="small"
              label="Period of time"
              value={period}
              onChange={(e) => {
                const nextPeriod = e.target.value;
                setPeriod(nextPeriod);
                setPage(0);

                if (nextPeriod === "Custom date") {
                  setCustomFrom("");
                  setCustomTo("");
                  return;
                }

                const range = getPeriodRange(nextPeriod);
                if (range) {
                  setCustomFrom(formatDateInput(range.from));
                  setCustomTo(formatDateInput(range.to));
                } else {
                  setCustomFrom("");
                  setCustomTo("");
                }
              }}
              sx={{ width: 220, minWidth: 220, ...reportFilterInputSx }}
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => (!selected ? "Select an option" : selected),
              }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {isCustomDate && (
              <>
                <TextField
                  size="small"
                  label="From"
                  type="date"
                  value={customFrom}
                  onChange={(e) => {
                    setCustomFrom(e.target.value);
                    setPage(0);
                  }}
                  sx={reportDateSx}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  label="To"
                  type="date"
                  value={customTo}
                  onChange={(e) => {
                    setCustomTo(e.target.value);
                    setPage(0);
                  }}
                  sx={reportDateSx}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}

            <Button
              variant="outlined"
              onClick={handleClear}
              disabled={loading}
              sx={{
                fontFamily: "Plus Jakarta Sans",
                fontSize: "12px",
                textTransform: "none",
                fontWeight: 700,
                height: "40px",
                padding: "12px",
                color: "#15202A",
                borderRadius: "10px",
                border: "none",
                "&:hover": {
                  border: "none",
                  backgroundColor: "transparent",
                },
              }}
            >
              <IconComponent icon="forward_media" size="12px" /> &nbsp;Clear
            </Button>

            <Button
              variant="outlined"
              onClick={handleGenerate}
              disabled={loading || !brokerOptionsLoaded || !brokerTreesLoaded}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                height: "40px",
                padding: "12px",
                color: "#1F2937",
                borderColor: "#374151",
                backgroundColor: "#F9FAFB",
                borderRadius: "10px",
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#111827",
                  backgroundColor: "#F3F4F6",
                  boxShadow: "none",
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} />
                  <span>Generating...</span>
                </Box>
              ) : (
                "Generate"
              )}
            </Button>

            <ExportIconButton
              disabled={!filteredRows.length || loading}
              onClick={handleExportClick}
              sx={exportButtonSx}
            >
              <IconComponent icon="download" size="16px" />
              Export
            </ExportIconButton>

            <Menu
              anchorEl={exportAnchorEl}
              open={exportOpen}
              onClose={handleExportClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={() => handleExport("csv")}>Export as CSV</MenuItem>
              <MenuItem onClick={() => handleExport("pdf")}>Export as PDF</MenuItem>
              <MenuItem onClick={() => handleExport("txt")}>Export as TXT</MenuItem>
            </Menu>
          </FiltersContainer>

          <TabsContainer>
            <Tabs
              value={tab}
              onChange={(_, v) => {
                setTab(v);
                setRows(getStoredReportRows(v === 0 ? "full" : "summary"));
                setError("");
                setSearch("");
                setPage(0);
              }}
              TabIndicatorProps={{
                children: <span className="MuiTabs-indicatorSpan" />,
              }}
            >
              <Tab label="Full report" />
              <Tab label="Summarized report" />
            </Tabs>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                marginLeft: "auto",
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search table..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                sx={{
                  ...BrokerStyles.InputStyles,
                  width: { xs: "100%", sm: 290 },
                  "& .MuiOutlinedInput-root": { borderRadius: "10px", height: 40 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Tooltip title="Filters">
                <IconButton
                  aria-label="Filters"
                  onClick={() => setShowScopeButtons((visible) => !visible)}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    border: "1px solid #C4C4C4",
                    color: "#6B7280",
                    backgroundColor: "#FFFFFF",
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: "#F9FAFB",
                      borderColor: "#B8B8B8",
                    },
                  }}
                >
                  <FilterListIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {showScopeButtons && (
                <>
                  <Button
                    variant={filterScope === "brokers" ? "contained" : "outlined"}
                    onClick={() => applyFilterScope("brokers")}
                    sx={{
                      height: 40,
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      boxShadow: "none",
                      color: filterScope === "brokers" ? "#FFFFFF" : "#15202A",
                      borderColor: "#C4C4C4",
                      backgroundColor: filterScope === "brokers" ? "#EB0000" : "#FFFFFF",
                      "&:hover": {
                        boxShadow: "none",
                        borderColor: filterScope === "brokers" ? "#EB0000" : "#15202A",
                        backgroundColor: filterScope === "brokers" ? "#D50000" : "#F9FAFB",
                      },
                    }}
                  >
                    All Brokers
                  </Button>

                  <Button
                    variant={filterScope === "ibs" ? "contained" : "outlined"}
                    onClick={() => applyFilterScope("ibs")}
                    sx={{
                      height: 40,
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      boxShadow: "none",
                      color: filterScope === "ibs" ? "#FFFFFF" : "#15202A",
                      borderColor: "#C4C4C4",
                      backgroundColor: filterScope === "ibs" ? "#EB0000" : "#FFFFFF",
                      "&:hover": {
                        boxShadow: "none",
                        borderColor: filterScope === "ibs" ? "#EB0000" : "#15202A",
                        backgroundColor: filterScope === "ibs" ? "#D50000" : "#F9FAFB",
                      },
                    }}
                  >
                    All IBs
                  </Button>
                </>
              )}
            </Box>
          </TabsContainer>

          {tab === 0 && (
            <TableContainerStyled>
              <Table sx={{ minWidth: tableMinWidth, tableLayout: "fixed" }}>
                <colgroup>
                  {FULL_REPORT_COLUMNS.map((column) => (
                    <col
                      key={column.field}
                      style={{ width: `${fullColumnWidths[column.field] || column.width}px` }}
                    />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow>
                    {FULL_REPORT_COLUMNS.map((column) => (
                      <TableCell
                        key={column.field}
                        sx={{
                          ...columnCellSx[column.field],
                          ...reportHeaderCellSx,
                          position: "relative",
                          pr: TABLE_COMPACT_HEADER_PADDING,
                          userSelect: "none",
                        }}
                      >
                        <Box
                          component="span"
                          title={column.label}
                          sx={{
                            display: "block",
                            lineHeight: "18px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {column.label}
                        </Box>
                        <Box
                          component="span"
                          role="separator"
                          aria-label={`Resize ${column.label} column`}
                          aria-orientation="vertical"
                          aria-valuemin={column.minWidth}
                          aria-valuenow={fullColumnWidths[column.field] || column.width}
                          tabIndex={0}
                          onPointerDown={(event) => handleColumnResizeStart(event, column)}
                          onKeyDown={(event) => handleColumnResizeKeyDown(event, column)}
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: "12px",
                            height: "100%",
                            cursor: "col-resize",
                            touchAction: "none",
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={12}>
                        <EmptyState>
                          <Box sx={{ minHeight: 180, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                            <CircularProgress size={32} />
                            <Typography variant="body2" color="text.secondary">Generating full report...</Typography>
                          </Box>
                        </EmptyState>
                      </TableCell>
                    </TableRow>
                  ) : paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12}>
                        <EmptyState>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                            <ErrorOutlineOutlinedIcon sx={{ fontSize: 40, color: "#475569" }} />
                            <Typography variant="body2" color="text.secondary">
                              No results yet. Set your filters to find matching requests.
                            </Typography>
                          </Box>
                        </EmptyState>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((row, index) => (
                      <TableRow key={`${row.ticket || row.account || index}-${index}`} sx={{ "& .MuiTableCell-root": reportBodyCellSx }}>
                        <TableCell sx={columnCellSx.date}>{row.date || "-"}</TableCell>
                        <TableCell sx={columnCellSx.ibId}>{row.user?.id ?? "-"}</TableCell>
                        <TableCell sx={columnCellSx.name}>{row.user?.name ?? "-"}</TableCell>
                        <TableCell sx={columnCellSx.email}>{row.user?.email ?? "-"}</TableCell>
                        <TableCell sx={columnCellSx.type}>{row.transaction_type ?? "-"}</TableCell>
                        <TableCell sx={columnCellSx.amount}>
                          {Number.isFinite(Number(row?.amount))
                            ? parseFloat(Number(row.amount).toFixed(4))
                            : (row?.amount ?? "-")}
                        </TableCell>
                        <TableCell sx={columnCellSx.volume}>
                          {String(row.transaction_type ?? "").trim().toUpperCase().includes("PAYOUT")
                            ? (row.volume ?? 0)
                            : "-"}
                        </TableCell>
                        <TableCell sx={columnCellSx.currency}>{row.currency ?? "-"}</TableCell>
                        <TableCell sx={columnCellSx.platform}>{row.platform ?? "-"}</TableCell>
                        <TableCell sx={columnCellSx.account}>{row.account ?? "-"}</TableCell>
                        <TableCell sx={columnCellSx.ticket}>{row.ticket ?? "-"}</TableCell>
                        <TableCell sx={columnCellSx.comments}>{row.comments ?? "-"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainerStyled>
          )}

          {tab === 1 && (
            <TableContainerStyled>
              <Table sx={{ minWidth: tableMinWidth, tableLayout: "fixed" }}>
                <colgroup>
                  {SUMMARY_REPORT_COLUMNS.map((column) => (
                    <col
                      key={column.field}
                      style={{ width: `${summaryColumnWidths[column.field] || column.width}px` }}
                    />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow>
                    {SUMMARY_REPORT_COLUMNS.map((column) => (
                      <TableCell
                        key={column.field}
                        sx={{
                          ...columnCellSx[column.field],
                          ...reportHeaderCellSx,
                          position: "relative",
                          userSelect: "none",
                        }}
                      >
                        {column.label}
                        <Box
                          component="span"
                          role="separator"
                          aria-label={`Resize ${column.label} column`}
                          tabIndex={0}
                          onPointerDown={(event) => handleColumnResizeStart(event, column)}
                          onKeyDown={(event) => handleColumnResizeKeyDown(event, column)}
                          sx={{ position: "absolute", top: 0, right: 0, width: "12px", height: "100%", cursor: "col-resize" }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <EmptyState>
                          <CircularProgress size={32} />
                        </EmptyState>
                      </TableCell>
                    </TableRow>
                  ) : paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <EmptyState>
                          <Typography variant="body2" color="text.secondary">
                            No results yet. Set your filters to find matching requests.
                          </Typography>
                        </EmptyState>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((row, index) => {
                      const volume = row?.payout_volume ?? row?.summary_per_currency?.[0]?.payout_volume;
                      return (
                        <TableRow key={`${row.user?.id || index}-${index}`} sx={{ "& .MuiTableCell-root": reportBodyCellSx }}>
                          <TableCell sx={columnCellSx.date}>{row.date || "-"}</TableCell>
                          <TableCell sx={columnCellSx.ibId}>{row.user?.id ?? "-"}</TableCell>
                          <TableCell sx={columnCellSx.name}>{row.user?.name ?? "-"}</TableCell>
                          <TableCell sx={columnCellSx.email}>{row.user?.email ?? "-"}</TableCell>
                          <TableCell sx={columnCellSx.walletCurrency}>{row.user?.wallet?.currency ?? "-"}</TableCell>
                          <TableCell sx={columnCellSx.walletBalance}>{formatWalletBalance(row.user?.wallet?.balance)}</TableCell>
                          <TableCell sx={columnCellSx.deposits}>{formatSummaryValueByCurrency(row.summary_per_currency, "deposits")}</TableCell>
                          <TableCell sx={columnCellSx.withdraws}>{formatSummaryValueByCurrency(row.summary_per_currency, "withdrawals")}</TableCell>
                          <TableCell sx={columnCellSx.payouts}>{formatSummaryValueByCurrency(row.summary_per_currency, "payouts")}</TableCell>
                          <TableCell sx={columnCellSx.payout_volume}>
                            {!volume || Number(volume) === 0 ? "-" : formatWalletBalance(volume)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainerStyled>
          )}

          {!loading && (
            <FooterContainer sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Typography variant="body2">
                Total: <b style={{ color: "#111827" }}>{totalCount}</b>
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                Pages:&nbsp;<b style={{ color: "#111827" }}>{totalPages}</b>
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#6B7280" }}>Rows:</Typography>
              <Select
                size="small"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                sx={{ height: 32, minWidth: 70, borderRadius: 2 }}
              >
                {ROWS_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Button size="small" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} sx={arrowBtnSx(page === 0)}>
                  <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                </Button>
                {pageItems.map((it, idx) =>
                  it === "…" ? (
                    <Typography key={`dots-${idx}`} sx={{ fontSize: 14, color: "#9CA3AF", px: 0.5 }}>…</Typography>
                  ) : (
                    <Button
                      key={`p-${it}`}
                      size="small"
                      onClick={() => setPage(it - 1)}
                      sx={{
                        minWidth: 28,
                        height: 28,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: it === page + 1 ? 900 : 700,
                        color: it === page + 1 ? "#111827" : "#6B7280",
                        backgroundColor: it === page + 1 ? "#EEF2FF" : "transparent",
                      }}
                    >
                      {it}
                    </Button>
                  )
                )}
                <Button size="small" disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} sx={arrowBtnSx(page >= totalPages - 1)}>
                  <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                </Button>
              </Box>
            </FooterContainer>
          )}
        </CardContainer>
      </PageContainer>
    </>
  );
};

export default Reports;